import { ChangeDetectorRef, Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { Config } from '@luggagehero/shared/environment';
import { BookableStorageLocation } from '@luggagehero/shared/interfaces';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { SharedStorageService, StorageTarget } from '@luggagehero/shared/services/storage';
import { SharedStorageCriteria } from '@luggagehero/shared/services/storage-criteria';
import { SharedUserService } from '@luggagehero/shared/services/users';

import { BaseComponent } from '../../../core';
import { RouterExtensions } from '../../../core/services/index';
import { StorageManagerService, TimeInterval } from '../../../services';
import {
  CreateManageableStorageLocation,
  ManageableStorageLocation,
} from '../../../services/storage-manager/storage-location';
import { NumberUtil } from '../../../utils/number.util';

@Component({ template: '' })
export abstract class ManageShopsBaseComponent extends BaseComponent implements OnInit {
  @Output() shopsChange = new EventEmitter<ManageableStorageLocation[]>();

  public shops: ManageableStorageLocation[];
  public currentShopId: string;
  public isAddingShop = false;
  public isEditingShop = false;
  public isLoading = false;

  private shopService = inject(SharedShopsService);

  constructor(
    private storageManagerService: StorageManagerService,
    private userService: SharedUserService,
    private _router: RouterExtensions,
    private cd: ChangeDetectorRef,
    private sharedStorageService: SharedStorageService,
  ) {
    super();
  }

  get isDebugMode(): boolean {
    return !Config.isProduction;
  }

  ngOnInit() {
    void this.loadShops();
  }

  async addDummyShop() {
    this.isLoading = true;

    const shop = CreateManageableStorageLocation();

    const types = ['Hotel', 'Kiosk', 'Gifts & Luggage', 'Bicycle Tours', 'Coffee & Bakery'];
    const type = types[Math.round(Math.random() * (types.length - 1))];
    shop.name = `${type} ${Math.round(Math.random() * 1000)} (test)`;

    const streets = ['Carrer de Valencia', 'Carrer de Mallorca', 'Gran Via', 'Passeig de Sant Joan', "Carrer d'Alaba"];
    const street = streets[Math.round(Math.random() * (streets.length - 1))];
    const number = Math.round(Math.random() * 299) + 1;
    shop.address = {
      city: 'Barcelona',
      country: 'Spain',
      countryCode: 'es',
      region: undefined,
      postal: '08003',
      street: `${number} ${street}`,
      formattedAddress: undefined,
    };

    const coordinates: [number, number] = [
      2.1734 + NumberUtil.random(0.001, 0.05),
      41.391 + NumberUtil.random(0.001, 0.05),
    ];

    shop.location = {
      coordinates,
    };
    shop.capacity = {
      hand: 0,
      normal: 25,
      maxCapacityForAdvanceBookings: 1,
    };
    shop.hasWifi = true;
    shop.openingHours.monday = [new TimeInterval(0, 1440)];
    shop.openingHours.tuesday = [new TimeInterval(0, 1440)];
    shop.openingHours.wednesday = [new TimeInterval(0, 1440)];
    shop.openingHours.thursday = [new TimeInterval(0, 1440)];
    shop.openingHours.friday = [new TimeInterval(0, 1440)];
    shop.openingHours.saturday = [new TimeInterval(0, 1440)];
    shop.openingHours.sunday = [new TimeInterval(0, 1440)];

    await this.storageManagerService.addStorageLocation(shop);

    await this.userService.getUserInfo();
    await this.loadShops();

    this.isLoading = false;
  }

  selectShop(shop: ManageableStorageLocation) {
    this.viewShopBookings(shop);
  }

  viewShopBookings(shop: ManageableStorageLocation) {
    void this._router.navigate(['storage-locations', shop._id, 'bookings']);
  }

  viewShopPricing(shop: ManageableStorageLocation) {
    void this._router.navigate(['storage-locations', shop._id, 'pricing']);
  }

  viewShopTransactions(shop: ManageableStorageLocation) {
    void this._router.navigate(['storage-locations', shop._id, 'transactions']);
  }

  viewInShopDisplay(shop: ManageableStorageLocation) {
    void this._router.navigate(['storage-locations', shop._id, 'in-store-display']);
  }

  viewTaxSettings(shop: ManageableStorageLocation) {
    void this._router.navigate(['storage-locations', shop._id, 'tax-settings']);
  }

  addShop() {
    void this._router.navigate(['storage-locations/new']);
  }

  editShop(shop: ManageableStorageLocation) {
    void this._router.navigate(['storage-locations', shop._id]);
  }

  isRecommended(shop: BookableStorageLocation): boolean {
    return this.shopService.isRecommended(shop);
  }

  public get isShopOwner(): boolean {
    return this.userService.user?.isShopOwner;
  }

  public get isImpersonationMode(): boolean {
    return this.userService.isImpersonationMode();
  }

  private async loadShops() {
    this.isLoading = true;
    try {
      if (this.userService.user.isShopOwner) {
        this.shops = await this.storageManagerService.getStorageLocations();
      } else if (this.userService.isImpersonationMode()) {
        if (this.sharedStorageService.get('storageLocationId', StorageTarget.Local)) {
          const storageLocationId = this.sharedStorageService.get('storageLocationId', StorageTarget.Local);
          const storageLocation = await this.storageManagerService.getStorageLocation(storageLocationId);
          this.shops = [storageLocation];
        }
      }

      this.shops.forEach((shop) => {
        // Set available status based current time
        const criteria = new SharedStorageCriteria(null, { from: new Date(), to: new Date() }, null);
        shop.available = this.shopService.isShopAvailable(shop, criteria, true);
      });

      this.shopsChange.emit(this.shops);
      this.cd.markForCheck();
    } catch {
      // TODO: Handle errors
    }
    this.isLoading = false;
  }
}
